import { z } from 'zod';
import { ColorSchema, ConditionSchema, LOOK, SourceRefSchema, about, numberFormat, sourceOf } from '../schema_fields.ts';
import { TileStyleSchema } from '../tile_style.ts';

// The status tile (docs/specs/status-tile.md, issue #17): its source's value shown as a state, a
// text or an icon; with a command, a tap changes it (a mode).

/** A value as a file writes it: a text, a number, or on and off. */
const PlainValueSchema = z.union([z.string(), z.number(), z.boolean()]);

/**
 * One state (ST-2): which values it is (`value`, else `when`, else every other), and how it shows.
 */
export const StatusStateSchema = z.object({
    value: PlainValueSchema.optional().meta(about('Value', 'the value this state is, a text or a number')),
    when: ConditionSchema.optional().meta(about('When', 'or a condition on a number, such as value > 5', 'condition')),
    text: z.string().optional().meta(about('Text', 'shown for it; the value itself when left out')),
    icon: z.string().optional().meta(about('Icon', 'by its name in the icon set', 'icon')),
    color: ColorSchema.optional().meta(about('Colour', 'of the icon and the text', 'color')),
    send: PlainValueSchema.optional().meta(about('Send', 'what choosing it sends, where that is not its value')),
    style: TileStyleSchema.optional().meta(about('Tile style', 'the tile\'s style while it is this state', 'tile-style')),
}).meta({ title: 'State' });

export const StatusOptionsSchema = z.object({
    // A light or thermostat read whole is shown by its main state (on or off, its mode): the tile's stateValueOf.
    source: sourceOf(SourceRefSchema, { gives: ['text', 'number', 'onOff', 'level', 'light', 'thermostat'], label: 'State' }),
    command: z.string().min(1).optional().meta(about('Command', 'what choosing a state sends, to change it; none to only show it', 'mode-command')),
    states: z.array(StatusStateSchema).default([]).meta(about('States', 'top to bottom: the first that matches the value is shown')),
    show: z.enum(['both', 'icon', 'text']).default('both').meta({ ...about('Show', 'the icon and the text, or one of them'), ...LOOK }),
    numberFormat: numberFormat('0.##'),
});

export type StatusState = z.infer<typeof StatusStateSchema>;
